import { ClickAwayListener, Tooltip, TooltipProps, Typography, useTheme } from '@material-ui/core';
import React, { FC } from 'react';
import styled from 'styled-components';
import { InlineLink } from '../buttons';
import { RouteName } from 'src/config/clientRoutes/types';
import { FontAwesomeSpriteButton } from '../FontAwesomeLocal';
import { SpriteIconsSolid } from 'src/config/api/models/FontAwesome';

type Props = Omit<TooltipProps, 'title'> & {
  title?: string; // | React.ReactNode;
  message?: string | React.ReactNode;
  clickAwayActive?: boolean;
  linkPath?: RouteName;
  condition?: boolean;
  disableHover?: boolean;
  linkText?: string;
  delay?: number;
  controlled?: boolean;
  opened?: boolean;
  noMaxWidth?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
};

type StyledProps = Omit<TooltipProps, 'title'> & {
  title?: any; // string;// | React.ReactNode;
  message?: string | React.ReactNode;
  clickAwayActive?: boolean;
  linkPath?: RouteName;
  condition?: boolean;
  disableHover?: boolean;
  linkText?: string;
  delay?: number;
  controlled?: boolean;
  opened?: boolean;
  noMaxWidth?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
};

const StyledTooltip = styled((props: TooltipProps & { noMaxWidth?: boolean }) => (
  <Tooltip arrow placement="top" classes={{ popper: props.className }} {...props} />
))`
  & .MuiTooltip-tooltip {
    min-width: 200px;
    max-width: ${({ noMaxWidth }) => (noMaxWidth ? 'auto' : '450px')};
    background-color: ${({ theme }) => theme.palette.common.white};
    color: ${({ theme }) => theme.palette.common.black};
    font-family: ${({ theme }) => theme.config.primaryFontFamily};
    font-size: ${({ theme }) => theme.typography.bodyXs.fontSize};
    border: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
    z-index: 100000;
  }

  & .MuiTooltip-arrow {
    color: ${({ theme }) => theme.palette.common.white};
    &::before {
      border: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
    }
  }
`;

const StyledTooltipWithRef = styled<FC<StyledProps>>(
  React.forwardRef<any, StyledProps>(({ title, className, noMaxWidth, ...restProps }, ref) => (
    <Tooltip
      title={title}
      arrow
      placement="top"
      classes={{ popper: className }}
      {...restProps}
      ref={ref}
    />
  ))
)`
& .MuiTooltip-tooltip {
  min-width: 200px;
  background-color: ${({ theme }) => theme.palette.common.white};
  color: ${({ theme }) => theme.palette.common.black};
  font-family: ${({ theme }) => theme.config.primaryFontFamily};
  font-size: ${({ theme }) => theme.typography.bodyXs.fontSize};
  border: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
}

& .MuiTooltip-arrow {
  color: ${({ theme }) => theme.palette.common.white};
  //transform-origin: ${(props) => props.placement || 'right'} center;
  &::before {
    border: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
  }

  & .MuiTooltip-arrow {
    color: ${({ theme }) => theme.palette.common.white};
    //transform-origin: ${(props) => props.placement || 'right'} center;
    &::before {
      border: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
    }
  }
`;

// TODO - URGENT!!! formwardRef this component so that we can inject tooltips in styled components!!!
const DMTooltip: FC<Props> = ({
  children,
  title,
  message,
  clickAwayActive = false,
  condition = true,
  linkPath,
  disableHover = true,
  linkText = 'link',
  delay = 0,
  controlled = false,
  opened = false,
  noMaxWidth = false,
  onClose = () => {},
  onOpen,
  ...restProps
}) => {
  const theme = useTheme();
  const tooltipContent = () => (
    <div style={{ padding: theme.spacing(1) }}>
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <div>
          {title && <Typography style={{ fontWeight: 600 }}>{title}</Typography>}
          <div
            style={{
              margin: theme.spacing(1, 0),
              fontWeight: 400,
              color: theme.palette.secondary.main,
              lineHeight: theme.typography.body1.lineHeight,
              whiteSpace: 'pre-wrap',
            }}
          >
            {message}
          </div>
        </div>
        {controlled && (
          <FontAwesomeSpriteButton
            buttonStyle={{
              alignSelf: 'start',
              cursor: 'pointer',
              marginLeft: title ? '0' : 'auto',
              padding: 0,
            }}
            icon={SpriteIconsSolid.XMARK}
            color={theme.palette.offblack}
            iconSize="sm"
            onClick={() => onClose()}
          />
        )}
      </div>

      {linkPath && (
        <InlineLink
          to={linkPath}
          underline="always"
          style={{ color: theme.palette.ternary.main, fontWeight: 600 }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText}
        </InlineLink>
      )}
    </div>
  );

  if (!condition) {
    return children;
  }

  if (controlled) {
    return (
      <ClickAwayListener onClickAway={clickAwayActive ? () => onClose() : () => {}}>
        <StyledTooltipWithRef
          interactive={true}
          open={opened}
          disableHoverListener={disableHover}
          onOpen={onOpen}
          leaveDelay={delay}
          title={tooltipContent()}
          {...restProps}
        >
          {children}
        </StyledTooltipWithRef>
      </ClickAwayListener>
    );
  }

  return (
    <StyledTooltip interactive={true} title={tooltipContent()} leaveDelay={delay} {...restProps}>
      {children}
    </StyledTooltip>
  );
};

export default React.memo(DMTooltip);
